/**
 * Narrow invite-logging redaction helpers.
 * Categories only — never returns paths, query strings, or invite codes.
 */

export type SafeInviteRouteCategory =
  | 'invite_route'
  | 'auth_with_invite'
  | 'auth'
  | 'profile'
  | 'public_home'
  | 'onboarding_with_invite'
  | 'onboarding'
  | 'network'
  | 'native_launch_url_present'
  | 'other';

export type SafeInviteLogAction =
  | 'saved'
  | 'read'
  | 'cleared'
  | 'rehydrate_skip_existing'
  | 'rehydrate_skip_no_code'
  | 'rehydrate_skip_in_flight'
  | 'rehydrate_start'
  | 'rehydrate_failed'
  | 'rehydrate_success'
  | 'rehydrate_error'
  | 'popup_saved'
  | 'popup_read'
  | 'context_saved'
  | 'context_read'
  | 'context_missing'
  | 'parse_error'
  | 'deeplink_received'
  | 'deeplink_navigating'
  | 'cold_start_invite'
  | 'deferred_invite_present'
  | 'deferred_invite_absent'
  | 'storage_snapshot';

/**
 * Classify a path/URL for logging. Never returns the original string.
 */
export function classifySafeInviteRoute(pathOrUrl: string): SafeInviteRouteCategory {
  const raw = String(pathOrUrl || '');
  let path = raw;
  try {
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) {
      const u = new URL(raw);
      // blissnet://invite/<code> → hostname invite
      if (u.protocol === 'blissnet:' && (u.hostname === 'invite' || u.pathname.startsWith('/invite'))) {
        return 'invite_route';
      }
      path = u.pathname + (u.search || '');
    }
  } catch {
    path = raw;
  }

  const pathname = path.split(/[?#]/)[0] || '';
  const hasInviteQuery = /(?:^|[?&])inviteCode=/i.test(path);

  if (pathname === '/invite' || pathname.startsWith('/invite/')) return 'invite_route';
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return 'profile';
  if (pathname === '/auth' || pathname.startsWith('/auth/')) {
    return hasInviteQuery ? 'auth_with_invite' : 'auth';
  }
  if (pathname === '/onboarding' || pathname.startsWith('/onboarding/')) {
    return hasInviteQuery ? 'onboarding_with_invite' : 'onboarding';
  }
  if (pathname === '/network' || pathname.startsWith('/network/')) return 'network';
  if (pathname === '/' || pathname === '') return 'public_home';
  return 'other';
}

/** Safe boolean presence of a native launch URL (never the URL itself). */
export function classifyNativeLaunchPresence(hasUrl: boolean): 'native_launch_url_present' | 'other' {
  return hasUrl ? 'native_launch_url_present' : 'other';
}

export function logInviteSafe(
  event: string,
  detail?: Record<string, string | boolean | number | undefined>
): void {
  if (!import.meta.env.DEV) return;
  if (detail) {
    console.info(event, detail);
  } else {
    console.info(event);
  }
}

/**
 * Redact OAuth secrets and invitation path/query values from URLs before logging.
 * Never returns a string containing a raw invite code segment.
 */
export function redactInviteSensitiveUrl(incomingUrl: string): string {
  const sensitiveKeys = [
    'code',
    'id_token',
    'access_token',
    'refresh_token',
    'state',
    'inviteCode',
  ];
  const redactParams = (params: URLSearchParams) => {
    sensitiveKeys.forEach((key) => {
      if (params.has(key)) {
        params.set(key, '[REDACTED]');
      }
    });
  };

  try {
    const parsed = new URL(String(incomingUrl));
    const host = parsed.hostname.toLowerCase();
    const isHttpsInvite =
      (host === 'blissnet.app' || host === 'www.blissnet.app') &&
      parsed.pathname.startsWith('/invite/');
    const isCustomSchemeInvite = parsed.protocol === 'blissnet:' && host === 'invite';
    if (isHttpsInvite || isCustomSchemeInvite) {
      return `${parsed.protocol}//${parsed.host}/invite/[REDACTED]`;
    }
    if (parsed.pathname.includes('/invite/')) {
      parsed.pathname = parsed.pathname.replace(/\/invite\/[^/]+/i, '/invite/[REDACTED]');
    }
    redactParams(parsed.searchParams);
    if (parsed.hash) {
      const hashParams = new URLSearchParams(parsed.hash.replace(/^#/, ''));
      redactParams(hashParams);
      const nextHash = hashParams.toString();
      parsed.hash = nextHash ? `#${nextHash}` : '';
    }
    return parsed.toString();
  } catch {
    return '[UNPARSEABLE_URL]';
  }
}
